import { Box, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { Documento, DynFormProps } from "..";
import { Controller, useFormContext } from "react-hook-form";
import SelectLocaisCobranca from "../../SelectsSimpleApiQuery/SelectLocaisCobranca";
import SelectDepartamentoFinanceiro from "../../SelectsSimpleApiQuery/SelectDepartamentoFinanceiro";
import BasicDatePicker from "../../BasicDatePicker";
import moment, { Moment } from "moment";
import SelectContaCorrente from "../../SelectsSimpleApiQuery/SelectContaCorrente";
import { formatarValorMonetario } from "../../../helpers/masks";
import { useMutation } from "react-query";
import axiosClient from "../../../lib/axios";
import { useEffect } from "react";

export default function DynAnaliseDoc({ spec, extras }: DynAnaliseDocProps) {
  const { control, setValue, getValues } = useFormContext();

  const dataEmissao = getValues(`${spec.name}.data_emissao`);
  const dataVencimento = getValues(`${spec.name}.data_vencimento`);

  const { mutateAsync: validacaoData, isLoading: isLoadingValidacaoData } = useMutation({
    mutationKey: 'validacao-data',
    mutationFn: (inputs: Moment) => axiosClient.post(`/validacao-data`, inputs, {
      params: {
        data: inputs?.format("DD-MM-YYYY"),
      },
    }),
    onError: (error) => {
      console.error("Erro na validação da data:", error);
    }
  });

  const titulo = {
    publico: {
      tipo_publico: extras?.tipo_publico ?? "",
      codigo_publico: extras?.codigo_publico ?? "",
      nome_pessoa: "",
    },
  };

  const isParcelado = extras?.is_parcelado === 'true';

  const parcelas = extras?.parcelas ?
    (Array.isArray(extras.parcelas) ?
      extras.parcelas.map((item: string) => {
        const parsedItem = JSON.parse(item);
        return {
          ...parsedItem,
          data_vencimento: moment(parsedItem.data_vencimento)
        };
      })
      : []
    )
    : [];

    useEffect(() => {
      if(extras?.data_emissao && !dataEmissao) {
        setValue(`${spec.name}.data_emissao`, moment(extras.data_emissao, "YYYY-MM-DD"));
      }
      if(extras?.data_vencimento && !dataVencimento) {
        setValue(`${spec.name}.data_vencimento`, moment(extras.data_vencimento, "YYYY-MM-DD"));
      }
    }, [extras, spec, dataEmissao, dataVencimento]);

  return (
    <Grid container spacing={2} mt={1}>
      <Grid size={{xs: 12, md: 6}}>
        <Controller
          name={`${spec.name}.local_cobranca`}
          control={control}
          rules={{ required: "Campo obrigatório" }}
          render={({ field, fieldState: { error, invalid } }) => (
            <SelectLocaisCobranca
              {...field}
              required
              error={invalid}
              value={field.value}
              onChange={(valor) => {
                field.onChange(valor);
              }}
            />
          )}
        />
      </Grid>

      <Grid size={{xs: 12, md: 6}}>
        <Controller
          name={`${spec.name}.departamento`}
          control={control}
          rules={{ required: "Campo obrigatório" }}
          render={({ field, fieldState: { error, invalid } }) => (
            <SelectDepartamentoFinanceiro
              {...field}
              required
              error={invalid}
              value={field.value}
              onChange={(valor) => field.onChange(valor)}
            />
          )}
        />
      </Grid>

      <Grid size={{xs: 12, md: 6}}>
        <Controller
          name={`${spec.name}.data_emissao`}
          control={control}
          rules={{
            required: "Campo obrigatório",
            validate: async (value) => {
              if (!value) return true;

              try {
                const dataMoment = moment(value, "YYYY-MM-DD");
                if (!dataMoment.isValid()) {
                  return "Data inválida";
                }

                const dataVencimento = getValues(`${spec.name}.data_vencimento`);
                if (dataVencimento) {
                  const dataVencimentoMoment = moment(dataVencimento, "YYYY-MM-DD");
                  if (dataMoment.isAfter(dataVencimentoMoment)) {
                    return "Data de emissão não pode ser maior que a data de vencimento";
                  }
                }

                if (isParcelado && parcelas.length > 0) {
                  const primeiraParcelaMoment = moment(parcelas[0].data_vencimento);
                  if (dataMoment.isAfter(primeiraParcelaMoment)) {
                    return "Data de emissão não pode ser maior que a data da primeira parcela";
                  }
                }

                const response = await validacaoData(dataMoment);

                if (response?.data === false) {
                  return response?.data?.message || "Data inválida";
                }

                return true;
              } catch (error: any) {
                console.error("Erro ao validar a data:", error);

                if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                  return "Erro de conexão. Verifique sua internet.";
                }

                return error?.response?.data?.message || "Erro ao validar a data";
              }
            }
          }}
          render={({ field, fieldState: { error, invalid } }) => (
            <BasicDatePicker
              label="Emissão"
              required
              helperText={error ? error.message : ""}
              error={invalid}
              disabled={isLoadingValidacaoData}
              value={
                field.value
                  ? moment(field.value, "YYYY-MM-DD")
                  : moment(extras?.data_emissao, "YYYY-MM-DD")
              }
              onChange={(data) => {
                if (data && moment(data).isValid()) {
                  field.onChange(moment(data).format("YYYY-MM-DD"));
                } else if (data === null) {
                  field.onChange(null);
                }
              }}
              formatString="DD/MM/YYYY"
            />
          )}
        />
      </Grid>

      {
        !isParcelado &&
        <Grid size={{xs: 12, md: 6}}>
          <Controller
            name={`${spec.name}.data_vencimento`}
            control={control}
            rules={{
              required: "Campo obrigatório",
              validate: async (value) => {
                if (!value) return true;

                const dataEmissao = getValues(`${spec.name}.data_emissao`);
                if (dataEmissao) {
                  const dataEmissaoMoment = moment(dataEmissao, "YYYY-MM-DD");
                  const dataVencimentoMoment = moment(value, "YYYY-MM-DD");
                  if (dataVencimentoMoment.isBefore(dataEmissaoMoment)) {
                    return "Data de vencimento não pode ser menor que a data de emissão";
                  }
                }

              }}}
            render={({ field, fieldState: { error, invalid } }) => (
              <BasicDatePicker
                label="Vencimento *"
                blockPastDates
                helperText={error ? error.message : ""}
                error={invalid}
                value={
                  field.value
                    ? moment(field.value, "YYYY-MM-DD")
                    : moment(extras?.data_vencimento, "YYYY-MM-DD")
                }
                onChange={(data) => {
                  field.onChange(data ? moment(data).format("YYYY-MM-DD") : null);
                }}
                formatString="DD/MM/YYYY"
              />
            )}
          />
        </Grid>
      }

      <Grid size={{xs: 12, md: 6}}>
        <Controller
          name={`${spec.name}.forma_pagamento`}
          control={control}
          render={({ field, fieldState: { error, invalid } }) => {
            const temContaBancaria = extras?.conta_bancaria ?? false; // Verifica se há conta bancária

            return (
              <FormControl fullWidth>
                <InputLabel id="select-forma-pagamento">
                  Forma de pagamento
                </InputLabel>
                <Select
                  labelId="select-forma-pagamento"
                  value={field.value}
                  label="Forma de pagamento"
                  onChange={(valor) => field.onChange(valor)}
                >
                  <MenuItem value="Não informado" disabled={!temContaBancaria}>
                    Não informado
                  </MenuItem>
                  <MenuItem
                    value="DOC/TED/Transferência"
                    disabled={!temContaBancaria}
                  >
                    DOC/TED/Transferência
                  </MenuItem>
                  <MenuItem value="Boleto" disabled={!temContaBancaria}>
                    Boleto
                  </MenuItem>
                  <MenuItem value="Cheque" disabled={!temContaBancaria}>
                    Cheque
                  </MenuItem>
                  <MenuItem value="Manual">Manual</MenuItem>
                  <MenuItem value="DDA" disabled={!temContaBancaria}>
                    DDA
                  </MenuItem>
                  <MenuItem value="Visto" disabled={!temContaBancaria}>
                    Visto
                  </MenuItem>
                </Select>
              </FormControl>
            );
          }}
        />
      </Grid>

      <Grid size={{xs: 12, md: 6}}>
        <Controller
          name={`${spec.name}.conta_corrente`}
          control={control}
          render={({ field }) => (
            <SelectContaCorrente
              allowClear
              value={field.value}
              onChange={(valor) => {
                field.onChange(valor);
                setValue(
                  `${spec.name}.banco_pagamento_id`,
                  valor?.banco?.id || null
                );
                setValue(`${spec.name}.agencia`, valor?.agencia || "");
                setValue(
                  `${spec.name}.agencia_digito`,
                  valor?.agencia_digito || ""
                );
                setValue(`${spec.name}.conta`, valor?.conta || "");
                setValue(
                  `${spec.name}.conta_digito`,
                  valor?.conta_digito || ""
                );
              }}
              titulo={titulo}
            />
          )}
        />
      </Grid>

      {isParcelado && parcelas.map((parcela: any, index: number) => (
        <Grid container size={{xs: 12}} key={index} spacing={2}>
          <Grid size={{xs: 12}}>
            <Box display={'flex'} alignItems={'center'} gap={2}>
              <Typography fontWeight={'bold'} variant="h6">{parcela.numero_parcela}°)</Typography>

              <Controller
                name={`${spec.name}.parcelas[${index}].data_vencimento`}
                control={control}
                rules={{ required: 'Campo obrigatório' }}
                defaultValue={parcela.data_vencimento}
                render={({ field, fieldState: { error, invalid } }) => (
                  <BasicDatePicker
                    label="Vencimento *"
                    blockPastDates
                    pastDates={index === 0 ? null : parcelas[index - 1]?.data_vencimento}
                    value={typeof field.value === 'string' ? moment(field.value) : field.value}
                    onChange={(data) => field.onChange(data)}
                    formatString="DD/MM/YYYY"
                    error={invalid}
                    helperText={error?.message}
                  />
                )}
              />

              <TextField
                disabled
                label="Valor"
                value={formatarValorMonetario(parcela.valor)}
              />
            </Box>
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
}

interface DynAnaliseDocProps extends DynFormProps {
  extras: Documento | null;
}
